import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@/components/ui/tooltip';
import { ArrowLeft, Search, Mail, Phone, Download, CheckCircle2, X, RefreshCw, Trash2, ShieldAlert, ImageOff, BadgeCheck, ShieldCheck, ChevronDown, ChevronRight, ExternalLink, Printer, FileText, Loader2, AlertTriangle } from 'lucide-react';
import BadgePrintDialog from '@/components/admin/BadgePrintDialog';
import BlankBadgePrintDialog from '@/components/admin/BlankBadgePrintDialog';
import ManualPaymentOverrideDialog from '@/components/admin/ManualPaymentOverrideDialog';
import ForceCheckInAllDialog from '@/components/admin/ForceCheckInAllDialog';
import CopyJsonButton from './CopyJsonButton';
import RegistrationsFilterDrawer, { EMPTY_FILTERS } from './components/RegistrationsFilterDrawer';
import { PresetChips, SavedViewsMenu, PRESETS } from './components/RegistrationsPresetsAndViews';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const STATUS_COLORS = {
  draft: 'bg-neutral-100 text-neutral-700',
  submitted: 'bg-blue-50 text-blue-800',
  pending_payment: 'bg-yellow-50 text-yellow-800',
  confirmed: 'bg-success-50 text-success-800',
  waitlisted: 'bg-secondary-50 text-secondary-800',
  cancelled: 'bg-red-50 text-red-800',
  checked_in: 'bg-primary-50 text-primary-800',
  no_show: 'bg-neutral-100 text-neutral-500',
};
const STATUS_OPTIONS = ['draft','submitted','pending_payment','confirmed','waitlisted','cancelled','checked_in','no_show'];

// ---------------------------------------------------------------------------
// 1c: Gender pill
// Brand tokens: male = primary (navy #2C3E50, cool), female = secondary
// (ember #D4A574, warm). Both are existing brand accents; no new palette.
// ---------------------------------------------------------------------------
const GenderPill = ({ gender }) => {
  const g = (gender || '').trim().toLowerCase();
  if (g !== 'male' && g !== 'female') return null;
  const styles = g === 'male'
    ? 'bg-primary-50 text-primary-800 border-primary-200'
    : 'bg-secondary-50 text-secondary-800 border-secondary-200';
  return (
    <Badge
      variant="outline"
      className={`${styles} h-5 text-[10px] px-1.5 shrink-0 capitalize`}
      data-testid={`regs-row-gender-pill-${g}`}
    >
      {g === 'male' ? 'M' : 'F'}
    </Badge>
  );
};

// ---------------------------------------------------------------------------
// 1c: Summary chip (top right of the RSVPs list).
// Compact, uses primary -> secondary brand gradient for the outer wrapper.
// ---------------------------------------------------------------------------
const SummaryChip = ({ summary, shownCount }) => {
  const total = summary?.total ?? 0;
  const vetted = summary?.vetted_count ?? 0;
  const withPhotos = summary?.with_photos_count ?? 0;
  const male = summary?.by_gender?.male || { count: 0, percentage: 0 };
  const female = summary?.by_gender?.female || { count: 0, percentage: 0 };
  const pct = (n) => total > 0 ? Math.round((n / total) * 100) : 0;
  return (
    <div
      className="rounded-xl p-[1px] bg-gradient-to-br from-primary-700 via-primary-500 to-secondary-500 shrink-0"
      data-testid="regs-summary-chip"
    >
      <div className="rounded-[10px] bg-white px-4 py-2.5 flex items-center gap-4">
        <div className="text-right pr-3 border-r border-neutral-200">
          <div className="text-2xl font-display font-bold text-primary-900 leading-none" data-testid="regs-summary-shown">
            {shownCount}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mt-0.5">
            Showing
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <SummaryStat label="Total" value={total} testid="regs-summary-total" />
          <SummaryStat
            label="Vetted"
            value={`${vetted} · ${pct(vetted)}%`}
            testid="regs-summary-vetted"
          />
          <SummaryStat
            label="w/ photo"
            value={`${withPhotos} · ${pct(withPhotos)}%`}
            testid="regs-summary-with-photos"
          />
          <div className="flex items-center gap-1.5 pl-2 border-l border-neutral-200" data-testid="regs-summary-gender">
            <Badge variant="outline" className="bg-primary-50 text-primary-800 border-primary-200 h-5 text-[10px] px-1.5 gap-1" data-testid="regs-summary-male">
              M {male.count} · {male.percentage}%
            </Badge>
            <Badge variant="outline" className="bg-secondary-50 text-secondary-800 border-secondary-200 h-5 text-[10px] px-1.5 gap-1" data-testid="regs-summary-female">
              F {female.count} · {female.percentage}%
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, testid }) => (
  <div className="flex flex-col items-start" data-testid={testid}>
    <span className="text-[10px] uppercase tracking-widest text-neutral-400">{label}</span>
    <span className="font-mono font-semibold text-primary-900">{value}</span>
  </div>
);

const PAYMENT_OPTIONS = [
  { value: 'not_required',                    label: 'Not required' },
  { value: 'pending',                         label: 'Pending' },
  { value: 'user_marked_paid',                label: 'Marked paid (guest)' },
  { value: 'auto_verified_pending_review',    label: 'Needs Review' },
  { value: 'verified',                        label: 'Verified / Paid' },
  { value: 'rejected',                        label: 'Rejected' },
  { value: 'failed',                          label: 'Failed' },
  { value: 'refunded',                        label: 'Refunded' },
];

const _initials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join('') || '?';
};

// Soft-fallback tile: browsers do not send Authorization headers on
// plain <img> requests. Signed URLs also expire (60min TTL). When the
// image fails to load, render a gradient card with the guest initials
// so the admin table never looks broken.
// C.1a: `photo.debug_url` (dev-only) is surfaced via a hover title so
// admins can eyeball the exact signed URL that failed. Only rendered
// when NODE_ENV !== 'production'.
const PhotoTile = ({ photo, index, guestName }) => {
  const [broken, setBroken] = React.useState(false);
  const src = photo?.thumb_url || photo?.url;
  const isDev = process.env.NODE_ENV !== 'production';
  const debugUrl = photo?.debug_url || photo?.url || photo?.thumb_url || '';
  const debugTitle = isDev && debugUrl ? `debug: ${debugUrl}` : undefined;
  if (broken || !src) {
    return (
      <div
        data-testid={`admin-photo-fallback-${index}`}
        data-debug-url={isDev ? debugUrl : undefined}
        className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-secondary-100 via-primary-50 to-neutral-100 flex items-center justify-center"
        title={debugTitle || 'Photo unavailable'}
      >
        <span className="text-secondary-800/80 font-semibold text-lg tracking-wider">
          {_initials(guestName)}
        </span>
      </div>
    );
  }
  return (
    <div
      className="aspect-square rounded-lg overflow-hidden bg-neutral-100"
      data-testid={`admin-photo-tile-${index}`}
      data-debug-url={isDev ? debugUrl : undefined}
      title={debugTitle}
    >
      <img
        data-testid={`admin-photo-${index}`}
        data-testid-debug-url={isDev ? debugUrl : undefined}
        src={src}
        alt={`photo-${index}`}
        loading="lazy"
        onError={() => {
          if (isDev) {
            console.warn('[admin-photo-tile-debug-url] load failed', {
              index, src, debugUrl,
            });
          }
          setBroken(true);
        }}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const AdminRegistrationsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'super_admin';
  const initialParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [event, setEvent] = useState(null);
  const [regs, setRegs] = useState([]);
  const [q, setQ] = useState(initialParams.get('q') || '');
  const [statusF, setStatusF] = useState(initialParams.get('status_filter') || '');
  const [paymentF, setPaymentF] = useState(initialParams.get('payment_filter') || '');
  const [hasPhotos, setHasPhotos] = useState(initialParams.get('has_photos') === '1');
  const [connectionIntent, setConnectionIntent] = useState(initialParams.get('intent') || '');
  const [interestLabel] = useState(initialParams.get('interest_label') || '');
  const [activityLabel] = useState(initialParams.get('activity_label') || '');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [badgeDialogOpen, setBadgeDialogOpen] = useState(false);
  const [blankBadgeDialogOpen, setBlankBadgeDialogOpen] = useState(false);
  const [manualOverrideReg, setManualOverrideReg] = useState(null);
  const [forceCheckInOpen, setForceCheckInOpen] = useState(false);
  const [drawerReg, setDrawerReg] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);   // registration object
  const [reissueFor, setReissueFor] = useState(null); // registration object (Force reissue)
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [markPaidBusy, setMarkPaidBusy] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [vetBusyId, setVetBusyId] = useState('');
  const [testBusyId, setTestBusyId] = useState('');

  // Bundle 71 - all 7 filter groups managed as a single object.
  // Initial value hydrates from URL query params for shareable links.
  const [filters, setFilters] = useState(() => {
    const next = { ...EMPTY_FILTERS };
    for (const k of Object.keys(EMPTY_FILTERS)) {
      const v = initialParams.get(k);
      if (v !== null) next[k] = v;
    }
    return next;
  });
  const [activePresetId, setActivePresetId] = useState(
    initialParams.get('preset') || null,
  );
  // Bundle 72 add-on - snapshot the preset's canonical filters at
  // apply-time so we can detect drift ("Unsaved changes" pill).
  const [presetSnapshot, setPresetSnapshot] = useState(null);
  const hasPresetDrift = useMemo(() => {
    if (!activePresetId || !presetSnapshot) return false;
    // Normalize both maps: only compare keys present in the snapshot,
    // and treat empty string == undefined.
    const eq = (a, b) => (a === '' || a === null || a === undefined)
      ? (b === '' || b === null || b === undefined)
      : String(a) === String(b);
    for (const k of Object.keys({ ...EMPTY_FILTERS, ...presetSnapshot })) {
      if (!eq(filters[k], presetSnapshot[k])) return true;
    }
    return false;
  }, [activePresetId, presetSnapshot, filters]);

  // Push filter state into the URL so it is deep-linkable.
  React.useEffect(() => {
    const p = new URLSearchParams(location.search);
    // Keep the non-filter params in the URL intact.
    for (const k of Object.keys(EMPTY_FILTERS)) p.delete(k);
    p.delete('preset');
    for (const [k, v] of Object.entries(filters)) {
      if (v !== '' && v !== null && v !== undefined) p.set(k, String(v));
    }
    if (activePresetId) p.set('preset', activePresetId);
    const next = p.toString();
    if (next !== location.search.replace(/^\?/, '')) {
      window.history.replaceState(null, '', `${location.pathname}?${next}`);
    }
  }, [filters, activePresetId, location.pathname, location.search]);

  const applyPreset = (id, presetFilters) => {
    setActivePresetId(id);
    setFilters({ ...EMPTY_FILTERS, ...presetFilters });
  };
  const applySavedView = (savedFilters) => {
    setActivePresetId(null);
    setFilters({ ...EMPTY_FILTERS, ...savedFilters });
  };
  const resetFilters = () => {
    setActivePresetId(null);
    setFilters({ ...EMPTY_FILTERS });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Bundle 71 - forward every non-empty filter param straight
      // through. Backend accepts unknown params as no-ops so this is
      // safe. `q`, `status_filter`, `payment_filter`, `intent`,
      // `interest_label`, `activity_label` stay as first-class fields
      // for backward-compat with saved URLs.
      const baseParams = {
        q: q || undefined,
        status_filter: statusF || undefined,
        payment_filter: paymentF || filters.payment_status || undefined,
        intent: connectionIntent || undefined,
        interest_label: interestLabel || undefined,
        activity_label: activityLabel || undefined,
      };
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined),
      );
      // Drop payment_status because it is already passed as payment_filter.
      delete filterParams.payment_status;
      const [ev, regsData] = await Promise.all([
        api.get(`/admin/events/${id}`),
        api.get(`/admin/events/${id}/registrations`, {
          params: { ...baseParams, ...filterParams },
        }),
      ]);
      setEvent(ev.data.event);
      setRegs(regsData.data.registrations || []);
      setPendingReviewCount(regsData.data.pending_review_count || 0);
      setSummary(regsData.data.summary || null);
      setSelectedIds(new Set());
    } finally { setLoading(false); }
  }, [id, q, statusF, paymentF, connectionIntent, interestLabel, activityLabel, filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Client-side filters for "hasPhotos" and connection intent (kept simple)
  const filtered = useMemo(() => {
    return regs.filter((r) => {
      if (hasPhotos && !(r.answers?.photos?.length)) return false;
      if (connectionIntent && !(r.answers?.intentions || []).includes(connectionIntent)) return false;
      return true;
    });
  }, [regs, hasPhotos, connectionIntent]);

  const toggleSelect = (rid) => {
    setSelectedIds((cur) => {
      const s = new Set(cur);
      if (s.has(rid)) s.delete(rid); else s.add(rid);
      return s;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((r) => r.id)));
  };

  const doBulk = async (action) => {
    if (selectedIds.size === 0) return;
    const labels = {
      mark_checked_in: 'Mark selected as checked-in?',
      mark_cancelled: 'Cancel selected registrations?',
      resend_confirmation: 'Resend confirmation emails to selected?',
    };
    if (!window.confirm(labels[action])) return;
    setBulkBusy(true);
    try {
      await api.post(`/admin/events/${id}/registrations/bulk`, { action, ids: Array.from(selectedIds) });
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk action failed');
    } finally { setBulkBusy(false); }
  };

  const downloadCsv = async () => {
    // Bundle 71 - forward the same filter params to the CSV endpoint
    // so the export matches what the admin sees on screen.
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (statusF) params.set('status_filter', statusF);
      if (paymentF) params.set('payment_filter', paymentF);
      for (const [k, v] of Object.entries(filters)) {
        if (v !== '' && v !== null && v !== undefined) params.set(k, String(v));
      }
      const url = `${api.defaults.baseURL}/admin/events/${id}/registrations.csv?${params.toString()}`;
      // Use fetch to include Authorization
      const token = localStorage.getItem('token');
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `registrations-${event?.slug || id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      alert('CSV download failed');
    }
  };

  // Bundle 71 - bulk-verify + bulk-nudge for the filter overhaul.
  const bulkVerify = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Verify payment for ${selectedIds.size} selected registration(s)? Purchase CAPI will fire.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        `/admin/events/${id}/registrations/bulk-verify`,
        { registration_ids: Array.from(selectedIds) },
      );
      alert(`Verified ${data.verified}. Skipped ${data.skipped?.length || 0}.`);
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk verify failed');
    } finally { setBulkBusy(false); }
  };
  const bulkNudge = async () => {
    if (selectedIds.size === 0) return;
    if (!isSuperAdmin) {
      alert('Bulk nudge requires super_admin.');
      return;
    }
    if (!window.confirm(`Send a nudge to ${selectedIds.size} selected registration(s)?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        `/admin/events/${id}/registrations/bulk-nudge`,
        { registration_ids: Array.from(selectedIds) },
      );
      alert(`Sent ${data.sent}. Rate-limited ${data.skipped_rate_limited || 0}. Unresolved ${data.unresolved?.length || 0}.`);
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk nudge failed');
    } finally { setBulkBusy(false); }
  };

  const openDrawer = async (r) => {
    try {
      const { data } = await api.get(`/admin/events/${id}/registrations/${r.id}`);
      setDrawerReg(data.registration);
    } catch (e) {
      setDrawerReg(r); // fallback to row data
    }
  };

  const hardDelete = async (reg) => {
    if (!window.confirm(`Hard-delete ${reg.email}? This permanently removes the record. Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/registrations/${reg.id}`);
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const hardDeleteCancelled = async () => {
    const cancelledSelected = filtered.filter(r => selectedIds.has(r.id) && r.status === 'cancelled').map(r => r.id);
    if (cancelledSelected.length === 0) {
      alert('Select at least one cancelled registration to delete.');
      return;
    }
    if (!window.confirm(`Hard-delete ${cancelledSelected.length} cancelled registrations? Cannot be undone.`)) return;
    try {
      await api.post('/admin/registrations/bulk-delete', { ids: cancelledSelected });
      setSelectedIds(new Set());
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk delete failed');
    }
  };

  // Phase D: admin confirms a Gmail-flagged auto-verify row. Hits the shared
  // event-scoped endpoint which runs the exact same code path as pure-manual
  // verify (seat lock + ticket + confirmation email).
  const confirmAutoVerify = async (reg) => {
    if (!window.confirm(`Confirm this Zelle payment and lock the seat for ${reg.email}?`)) return;
    setConfirmBusy(true);
    try {
      await api.post(`/admin/events/${id}/registrations/${reg.id}/confirm`, {
        admin_note: 'Confirmed from RSVPs list (Phase D)',
      });
      await fetchAll();
      setDrawerReg(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Confirm failed');
    } finally { setConfirmBusy(false); }
  };

  // 1c: toggle admin_vetted flag on a registration. Optimistically flips
  // in-place so the row responds immediately, then re-syncs the list to
  // pick up the fresh summary counts.
  const toggleVetted = async (reg) => {
    setVetBusyId(reg.id);
    const next = !reg.admin_vetted;
    setRegs((cur) => cur.map((row) => row.id === reg.id
      ? { ...row, admin_vetted: next } : row));
    try {
      await api.post(`/admin/registrations/${reg.id}/vet`, { vetted: next });
      await fetchAll();
    } catch (e) {
      // rollback
      setRegs((cur) => cur.map((row) => row.id === reg.id
        ? { ...row, admin_vetted: !next } : row));
      alert(e?.response?.data?.detail || 'Vet toggle failed');
    } finally { setVetBusyId(''); }
  };

  // Batch 5: toggle is_test flag. Test-flagged rows stay visible in the
  // list (with a Test badge + muted background) but are excluded from
  // the summary + analytics tallies. Optimistic + re-sync just like
  // toggleVetted so the SummaryChip repaints once counts land.
  const toggleTest = async (reg) => {
    setTestBusyId(reg.id);
    const next = !reg.is_test;
    setRegs((cur) => cur.map((row) => row.id === reg.id
      ? { ...row, is_test: next } : row));
    try {
      await api.post(`/admin/registrations/${reg.id}/mark-test`, { is_test: next });
      await fetchAll();
    } catch (e) {
      setRegs((cur) => cur.map((row) => row.id === reg.id
        ? { ...row, is_test: !next } : row));
      alert(e?.response?.data?.detail || 'Test toggle failed');
    } finally { setTestBusyId(''); }
  };

  // C.1b: manually attach a verified payment_transaction to a stuck RSVP.
  // Runs the SAME `_finalize_registration_on_paid` finalizer used by
  // Stripe webhooks + Gmail auto-verify, so ticket + email + points fire
  // once. Idempotent server-side: 409 if a paid txn already exists.
  const markRegistrationPaid = async (payload) => {
    if (!drawerReg) return;
    setMarkPaidBusy(true);
    try {
      const { data } = await api.post(
        `/admin/registrations/${drawerReg.id}/mark-paid`, payload,
      );
      await fetchAll();
      try {
        const r = await api.get(
          `/admin/events/${id}/registrations/${drawerReg.id}`,
        );
        setDrawerReg(r.data.registration);
      } catch { /* keep current */ }
      alert(
        `Marked paid. Ticket ${data.ticket_code || '(pending)'} issued to ${drawerReg.email}.`
      );
    } catch (e) {
      alert(e?.response?.data?.detail || 'Mark paid failed');
    } finally { setMarkPaidBusy(false); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <Link to={`/admin/events/${id}/edit`} className="text-sm text-neutral-500 hover:text-primary-800 inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Registrations</h1>
          <p className="text-neutral-600 text-sm">{event?.title}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/admin/events/${id}/nudge`}
            className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-900 hover:bg-primary-100 transition"
            data-testid="admin-regs-nudge-link"
          >
            <Mail className="h-4 w-4" /> Nudge unregistered
          </Link>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBadgeDialogOpen(true)}
            data-testid="admin-regs-print-badges-btn"
          >
            <Printer className="h-4 w-4 mr-1" /> Print badges
          </Button>
          {isSuperAdmin ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBlankBadgeDialogOpen(true)}
              data-testid="admin-regs-print-blank-badges-btn"
            >
              <Printer className="h-4 w-4 mr-1" /> Print blank badges
            </Button>
          ) : null}
          {isSuperAdmin ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setForceCheckInOpen(true)}
              className="border-red-400 text-red-800 hover:bg-red-50"
              data-testid="admin-regs-force-check-in-all-btn"
            >
              <ShieldAlert className="h-4 w-4 mr-1" /> Force check in all
            </Button>
          ) : null}
          <SummaryChip summary={summary} shownCount={filtered.length} />
        </div>
      </div>

      {/* Bundle 71 - workflow presets. Click applies filter set. */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <PresetChips
          onApply={applyPreset}
          activePresetId={activePresetId}
        />
        {hasPresetDrift ? (
          <div
            className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
            data-testid="preset-drift-pill"
          >
            <span>Unsaved changes</span>
            <button
              type="button"
              onClick={async () => {
                const name = window.prompt(
                  'Name this new saved view (based on the edits you made to the preset):',
                  `${(PRESETS[activePresetId]?.label) || activePresetId} (custom)`,
                );
                if (!name || !name.trim()) return;
                try {
                  await api.post('/admin/saved-views', {
                    page: 'registrations',
                    name: name.trim(),
                    filter_json: filters,
                    event_id: id,
                  });
                  alert(`Saved view "${name.trim()}"`);
                  setPresetSnapshot({ ...filters });
                } catch (e) {
                  alert(e?.response?.data?.detail || 'Save failed');
                }
              }}
              className="rounded-full bg-amber-800 text-white px-2 py-0.5 text-[10px] font-semibold hover:bg-amber-900 transition-colors"
              data-testid="preset-drift-save-btn"
            >
              Save as new view
            </button>
          </div>
        ) : null}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input placeholder="Search name, email, phone, external_id, txn ref…" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && fetchAll()}
                 className="pl-9"
                 data-testid="regs-search-input" />
        </div>
        <RegistrationsFilterDrawer
          value={filters}
          onChange={setFilters}
          onApply={fetchAll}
          onReset={() => { resetFilters(); }}
        />
        <SavedViewsMenu
          page="registrations" eventId={id}
          currentFilters={filters}
          onApply={(f) => { applySavedView(f); }}
        />
        <Button variant="outline" onClick={fetchAll} data-testid="regs-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={downloadCsv} data-testid="regs-csv-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
        {pendingReviewCount > 0 && (
          <button
            type="button"
            onClick={() => setPaymentF(paymentF === 'auto_verified_pending_review' ? '' : 'auto_verified_pending_review')}
            data-testid="regs-needs-review-chip"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-semibold transition-all
              ${paymentF === 'auto_verified_pending_review'
                ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                : 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100'}`}
            title="Show only RSVPs where the Gmail poller matched a payment but a host hasn't confirmed yet"
          >
            <ShieldAlert className="h-4 w-4" />
            Needs Review ({pendingReviewCount})
          </button>
        )}
      </div>

      {/* Bulk toolbar */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-2 p-3 rounded-lg border border-secondary-200 bg-secondary-50/40">
          <span className="text-sm font-medium text-primary-900">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => doBulk('mark_checked_in')} disabled={bulkBusy} data-testid="regs-bulk-checkin">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Check in
          </Button>
          <Button size="sm" variant="outline" onClick={() => doBulk('mark_cancelled')} disabled={bulkBusy} data-testid="regs-bulk-cancel">
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button size="sm" variant="outline" onClick={() => doBulk('resend_confirmation')} disabled={bulkBusy} data-testid="regs-bulk-resend">
            <Mail className="h-3.5 w-3.5 mr-1" /> Resend email
          </Button>
          {/* Bundle 71 - bulk-verify + bulk-nudge + CSV export of selection */}
          <Button size="sm" variant="outline" onClick={bulkVerify} disabled={bulkBusy}
                  data-testid="regs-bulk-verify"
                  className="border-emerald-200 text-emerald-800 hover:bg-emerald-50">
            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify paid
          </Button>
          {isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={bulkNudge} disabled={bulkBusy}
                    data-testid="regs-bulk-nudge"
                    className="border-secondary-200 text-secondary-800 hover:bg-secondary-50">
              <Mail className="h-3.5 w-3.5 mr-1" /> Send nudge
            </Button>
          )}
          {isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={hardDeleteCancelled} disabled={bulkBusy}
                    className="text-accent-700 hover:text-accent-900 hover:bg-accent-50 border-accent-200"
                    data-testid="regs-bulk-delete-cancelled-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete cancelled
            </Button>
          )}
        </div>
      )}

      <Card className="border shadow-soft">
        <CardContent className="p-0 divide-y divide-neutral-100">
          {loading ? (
            <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No registrations match these filters.</div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-2 bg-neutral-50/60 text-xs uppercase tracking-widest text-neutral-500">
                <Checkbox checked={selectedIds.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} data-testid="regs-select-all" />
                <span>Select all</span>
              </div>
              {filtered.map((r) => {
                const pt = r.payment_txn;
                const needsReview = pt?.payment_status === 'auto_verified_pending_review';
                return (
                <div
                  key={r.id}
                  className={`p-4 flex items-center gap-3 hover:bg-neutral-50/60 cursor-pointer ${needsReview ? 'bg-amber-50/40' : ''}`}
                  data-testid={`regs-row-${r.id}`}
                >
                  <Checkbox
                    checked={selectedIds.has(r.id)}
                    onCheckedChange={() => toggleSelect(r.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`regs-row-check-${r.id}`}
                  />
                  <div onClick={() => openDrawer(r)} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white font-semibold text-sm">
                      {((r.answers?.preferred_first_name || r.answers?.full_name || r.email || '?')[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-primary-900 truncate flex items-center gap-1.5">
                        {r.user_id ? (
                          <Link
                            to={`/admin/users/${r.user_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="truncate hover:text-secondary-700 hover:underline"
                            data-testid={`regs-row-user-link-${r.id}`}
                            title="Open user profile"
                          >
                            {r.answers?.full_name || r.email}
                          </Link>
                        ) : (
                          <span className="truncate">{r.answers?.full_name || r.email}</span>
                        )}
                        <GenderPill gender={r.answers?.gender} />
                        {r.computed_age != null && (
                          <Badge
                            variant="outline"
                            className="bg-neutral-50 text-neutral-700 border-neutral-200 h-5 text-[10px] px-1.5 shrink-0"
                            data-testid={`regs-row-age-${r.id}`}
                            title="Age computed from DOB"
                          >
                            {r.computed_age}
                          </Badge>
                        )}
                        {r.is_test && (
                          <Badge
                            variant="outline"
                            className="bg-neutral-100 text-neutral-700 border-neutral-300 gap-1 shrink-0 h-5 text-[10px] px-1.5"
                            data-testid={`regs-row-test-badge-${r.id}`}
                            title="Flagged as a test registration. Excluded from summary + analytics tallies."
                          >
                            Test
                          </Badge>
                        )}
                        {r.admin_vetted && (
                          <Badge
                            variant="outline"
                            className="bg-success-50 text-success-800 border-success-200 gap-1 shrink-0 h-5 text-[10px] px-1.5"
                            data-testid={`regs-row-vetted-badge-${r.id}`}
                            title="Manually reviewed and vetted by an admin"
                          >
                            <BadgeCheck className="h-3 w-3" /> Vetted
                          </Badge>
                        )}
                        {r.has_profile_photos === false && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center text-neutral-400 shrink-0"
                                  data-testid={`regs-row-no-photo-${r.id}`}
                                >
                                  <ImageOff className="h-3.5 w-3.5" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                No profile picture on file
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {/* Bundle 154 - red-dot indicator when this
                            registration is missing photos OR socials.
                            Sources: server-side backfill flag
                            (`needs_backfill_photo_or_social`) for
                            legacy rows, OR a live derivation from
                            the answers payload for freshly-loaded
                            rows. Tooltip surfaces which fields are
                            missing so organizers know what to ask. */}
                        {(() => {
                          const ans = r.answers || {};
                          const socialEntries = ans.social_handles && typeof ans.social_handles === 'object'
                            ? Object.values(ans.social_handles).filter((v) => typeof v === 'string' && v.trim())
                            : [];
                          const missingPhotos = !(Array.isArray(ans.photos) && ans.photos.length >= 1);
                          const missingSocials = socialEntries.length < 1;
                          const flagged = !!r.needs_backfill_photo_or_social || missingPhotos || missingSocials;
                          if (!flagged) return null;
                          const parts = [];
                          if (missingPhotos || r.backfill_missing_photos) parts.push('pictures');
                          if (missingSocials || r.backfill_missing_socials) parts.push('social handle');
                          const label = parts.length
                            ? `Missing required RSVP fields: ${parts.join(' + ')}`
                            : 'Missing required RSVP fields';
                          return (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center shrink-0"
                                    data-testid={`regs-row-mandate-dot-${r.id}`}
                                  >
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {label}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                      </div>
                      <div className="text-xs text-neutral-500 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</span>
                        {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>}
                        {r.answers?.city && <span>{r.answers.city}</span>}
                      </div>
                    </div>
                    {needsReview && (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-800 border-amber-300 gap-1 shrink-0"
                              data-testid={`regs-row-needs-review-${r.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ShieldAlert className="h-3 w-3" /> Needs Review
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs text-xs">
                            <div className="space-y-1">
                              <div><span className="font-semibold">Service:</span> <span className="capitalize">{pt.auto_verify_service || (pt.auto_verify_bank_pattern_id === 'venmo' ? 'venmo' : 'zelle')}</span></div>
                              <div><span className="font-semibold">Gmail matched:</span> {pt.auto_verify_matched_at ? new Date(pt.auto_verify_matched_at).toLocaleString() : '-'}</div>
                              <div><span className="font-semibold">Amount:</span> ${(pt.auto_verify_matched_amount ?? 0).toFixed(2)} (expected ${((pt.amount_cents || 0) / 100).toFixed(2)})</div>
                              <div><span className="font-semibold">Reference:</span> <code>{pt.auto_verify_matched_reference || pt.manual_reference || '-'}</code></div>
                              <div><span className="font-semibold">Pattern:</span> {pt.auto_verify_bank_pattern_id || '-'}</div>
                              <div className="text-neutral-400"><span className="font-semibold">Gmail msg:</span> <code>{(pt.auto_verify_gmail_message_id || '').slice(0, 12)}...</code></div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <Badge
                      variant="outline"
                      className={`capitalize ${needsReview ? 'bg-amber-50 text-amber-800 border-amber-300' : STATUS_COLORS[r.status] || STATUS_COLORS.draft}`}
                      data-testid={`regs-row-status-${r.id}`}
                    >
                      {needsReview ? 'Awaiting confirmation' : (r.status || 'draft').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {needsReview && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); confirmAutoVerify(r); }}
                        disabled={confirmBusy}
                        className="bg-success-600 hover:bg-success-700 text-white h-8"
                        data-testid={`regs-row-confirm-${r.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setRejectFor(r); }}
                        disabled={confirmBusy}
                        className="h-8"
                        data-testid={`regs-row-reject-${r.id}`}
                      >
                        <X className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  {r.status === 'confirmed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post(`/admin/events/${id}/registrations/${r.id}/resend-ticket`);
                          alert(`Ticket resent to ${r.email}`);
                        } catch (err) {
                          alert(err?.response?.data?.detail || 'Resend failed');
                        }
                      }}
                      className="h-8 shrink-0"
                      title="Resend ticket email"
                      data-testid={`regs-row-resend-ticket-${r.id}`}
                    >
                      <Mail className="h-3.5 w-3.5 mr-1" /> Resend ticket
                    </Button>
                  )}
                  {isSuperAdmin
                    && r.status !== 'cancelled'
                    && r.status !== 'rejected'
                    && r.payment_status !== 'verified'
                    && r.payment_status !== 'paid'
                    && r.payment_status !== 'waived' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setManualOverrideReg(r);
                      }}
                      className="h-8 shrink-0 bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                      title="Mark payment paid manually (super-admin)"
                      data-testid={`regs-row-mark-paid-manual-${r.id}`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                      Mark paid (manual)
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); toggleVetted(r); }}
                    disabled={vetBusyId === r.id}
                    className={`h-8 shrink-0 ${r.admin_vetted
                      ? 'bg-success-50 text-success-800 border-success-300 hover:bg-success-100'
                      : 'text-neutral-700 hover:bg-neutral-50'}`}
                    title={r.admin_vetted ? 'Un-vet (mark as needing review again)' : 'Mark as vetted'}
                    data-testid={`regs-row-vet-toggle-${r.id}`}
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    {r.admin_vetted ? 'Vetted' : 'Vet'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); toggleTest(r); }}
                    disabled={testBusyId === r.id}
                    className={`h-8 shrink-0 ${r.is_test
                      ? 'bg-neutral-200 text-neutral-800 border-neutral-400 hover:bg-neutral-300'
                      : 'text-neutral-600 hover:bg-neutral-50'}`}
                    title={r.is_test
                      ? 'Unmark test. Row will rejoin the summary + analytics tallies.'
                      : 'Mark as test. Row stays visible but is excluded from tallies.'}
                    data-testid={`regs-row-test-toggle-${r.id}`}
                  >
                    {r.is_test ? 'Untest' : 'Test'}
                  </Button>
                  {isSuperAdmin && r.status === 'confirmed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setReissueFor(r); }}
                      className={`h-8 shrink-0 ${r.ticket_code
                        ? 'text-secondary-800 border-secondary-300 hover:bg-secondary-50'
                        : 'text-accent-800 border-accent-300 hover:bg-accent-50'}`}
                      title={r.ticket_code
                        ? 'Force reissue the confirmation email with the existing ticket code'
                        : 'Confirmed but ticket_code is missing. Mint one and send.'}
                      data-testid={`regs-row-force-reissue-${r.id}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      {r.ticket_code ? 'Force reissue' : 'Force reissue (no ticket!)'}
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); hardDelete(r); }}
                      className="text-accent-700 hover:text-accent-900 hover:bg-accent-50 shrink-0"
                      title="Hard-delete registration"
                      data-testid={`regs-row-delete-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                );
              })}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <Sheet open={!!drawerReg} onOpenChange={(v) => { if (!v) setDrawerReg(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <div className="flex items-start justify-between gap-3">
              <SheetTitle>Registration detail</SheetTitle>
              {drawerReg?.linked_user_id ? (
                <Link
                  to={`/admin/users/${drawerReg.linked_user_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-secondary-300 bg-secondary-50 text-secondary-800 hover:bg-secondary-100 px-2.5 py-1 text-xs font-semibold shrink-0"
                  data-testid="regs-detail-view-user-link"
                  onClick={() => setDrawerReg(null)}
                >
                  View full user profile
                  <ExternalLink className="h-3 w-3" />
                </Link>
              ) : drawerReg ? (
                <span
                  className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-600 px-2.5 py-1 text-xs shrink-0"
                  data-testid="regs-detail-guest-chip"
                >
                  Guest RSVP (not yet claimed)
                </span>
              ) : null}
            </div>
          </SheetHeader>
          {drawerReg && (
            <RegistrationDetail
              reg={drawerReg}
              onConfirm={async () => { await confirmAutoVerify(drawerReg); setDrawerReg(null); }}
              confirmBusy={confirmBusy}
              onMarkPaid={markRegistrationPaid}
              markPaidBusy={markPaidBusy}
              onForceReissue={(r) => setReissueFor(r)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Phase D: Reject dialog */}
      <RejectDialog
        reg={rejectFor}
        onClose={() => setRejectFor(null)}
        onSubmitted={async () => { setRejectFor(null); await fetchAll(); }}
        eventId={id}
      />

      {/* 1b: Force reissue dialog (super_admin only) */}
      <ReissueTicketDialog
        reg={reissueFor}
        onClose={() => setReissueFor(null)}
        onSubmitted={async () => { setReissueFor(null); await fetchAll(); }}
      />

      {/* Bundle 66 - Print Badges dialog */}
      <BadgePrintDialog
        open={badgeDialogOpen}
        onClose={() => setBadgeDialogOpen(false)}
        eventId={id}
        selectedIds={Array.from(selectedIds)}
        filteredIds={filtered.map((r) => r.id)}
      />

      {/* Bundle 173 - Blank Badges dialog (super-admin) */}
      {/* Bundle 175 hotfix (2026-02): also null-guard on `event` so
          the dialog tree never mounts with an undefined event on
          first render. */}
      {isSuperAdmin && event ? (
        <BlankBadgePrintDialog
          open={blankBadgeDialogOpen}
          onClose={() => setBlankBadgeDialogOpen(false)}
          eventId={id}
          eventSlug={event?.slug}
        />
      ) : null}

      {/* Bundle 173 - Manual payment override (super-admin) */}
      {/* Bundle 175 hotfix (2026-02): also null-guard on `event`. */}
      {isSuperAdmin && manualOverrideReg && event ? (
        <ManualPaymentOverrideDialog
          open={!!manualOverrideReg}
          registration={manualOverrideReg}
          event={event}
          onClose={() => setManualOverrideReg(null)}
          onSuccess={async () => {
            setManualOverrideReg(null);
            await fetchAll();
          }}
        />
      ) : null}

      {/* Bundle 175 - Force check in all (super-admin) */}
      {/* Bundle 175 hotfix (2026-02): earlier version passed
          `registrations={rows}` - an undefined variable that only
          evaluated when the ternary rendered (i.e. only for
          super-admins), triggering a ReferenceError and the
          ErrorBoundary on prod. The correct source is `regs`
          (the full server-fetched list). Also add null-guard on
          `event` so an initial render before `fetchAll()` completes
          cannot crash the dialog tree either. */}
      {isSuperAdmin && event ? (
        <ForceCheckInAllDialog
          open={forceCheckInOpen}
          onClose={() => setForceCheckInOpen(false)}
          event={event}
          adminEmail={me?.email}
          registrations={regs}
          onSuccess={async () => {
            setForceCheckInOpen(false);
            await fetchAll();
          }}
        />
      ) : null}
    </div>
  );
};

const RegistrationDetail = ({ reg, onConfirm, confirmBusy, onMarkPaid, markPaidBusy, onForceReissue }) => {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'super_admin';
  const a = reg.answers || {};
  const needsReview = reg.payment_txn?.payment_status === 'auto_verified_pending_review';
  // C.1b Mark-paid eligibility: submitted or pending_payment RSVP with no
  // verified/paid txn, and NOT already in the auto-verify review flow (that
  // has its own Confirm/Reject buttons above).
  const pt = reg.payment_txn || {};
  const paidStates = new Set(['verified', 'paid', 'waived']);
  const eligibleStatus = ['submitted', 'pending_payment'].includes(reg.status);
  const showMarkPaid = eligibleStatus && !needsReview && !paidStates.has(pt.payment_status);
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Public QR endpoint (ticket_service.qr_public_url) - no auth required.
  const qrSrc = reg.ticket_code
    ? `${backend}/api/tickets/public-qr/${reg.id}.png`
    : null;
  return (
    <div className="mt-4 space-y-4 text-sm">
      {needsReview && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
          data-testid="regs-detail-awaiting-banner"
        >
          <div className="flex items-start gap-2 text-amber-900">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">Awaiting confirmation</div>
              <div className="text-xs text-amber-800/90">
                Payment was auto-verified from bank alerts. Confirm to finalize this registration.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={confirmBusy}
            className="bg-success-600 hover:bg-success-700 text-white"
            data-testid="regs-detail-confirm-payment"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm payment
          </Button>
        </div>
      )}

      {showMarkPaid && (
        <MarkPaidPanel
          reg={reg}
          onSubmit={onMarkPaid}
          busy={markPaidBusy}
        />
      )}

      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white font-semibold">
          {((a.preferred_first_name || a.full_name || reg.email || '?')[0] || '?').toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-primary-900">{a.full_name || reg.email}</div>
          <div className="text-xs text-neutral-500">{reg.email} · {reg.phone || 'no phone'}</div>
        </div>
      </div>

      {reg.status === 'confirmed' && qrSrc && (
        <div className="rounded-lg border border-neutral-200 p-3 space-y-2" data-testid="regs-detail-ticket-block">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Ticket QR</div>
          <div className="flex items-start gap-3">
            <img
              src={qrSrc}
              alt="Ticket QR"
              className="w-32 h-32 rounded bg-white border border-neutral-200"
              data-testid="regs-detail-ticket-qr"
            />
            <div className="text-xs text-neutral-600 space-y-1">
              <div className="font-mono font-semibold text-secondary-700">
                {reg.ticket_code}
              </div>
              <a
                href={qrSrc}
                download={`ticket-${reg.ticket_code}.png`}
                data-testid="regs-detail-ticket-download"
                className="inline-block px-2 py-1 rounded border border-neutral-200 hover:bg-neutral-50 text-neutral-700"
              >
                Download PNG
              </a>
              {isSuperAdmin && (
                <div>
                  <button
                    type="button"
                    onClick={() => onForceReissue?.(reg)}
                    className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded border border-secondary-300 text-secondary-800 hover:bg-secondary-50 text-xs"
                    data-testid="regs-detail-force-reissue"
                    title="Re-send confirmation email; ticket_code stays the same"
                  >
                    <RefreshCw className="h-3 w-3" /> Force reissue
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {reg.status === 'confirmed' && !reg.ticket_code && isSuperAdmin && (
        <div
          className="rounded-lg border border-accent-300 bg-accent-50 p-3 flex items-start justify-between gap-3"
          data-testid="regs-detail-force-reissue-missing-banner"
        >
          <div className="text-xs text-accent-900">
            <div className="font-semibold">Ticket code is missing.</div>
            <div>This confirmed registration never got a ticket. Reissue to mint one and email it.</div>
          </div>
          <Button
            size="sm"
            onClick={() => onForceReissue?.(reg)}
            className="bg-accent-700 hover:bg-accent-800 text-white h-8 shrink-0"
            data-testid="regs-detail-force-reissue-no-ticket"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Force reissue (no ticket!)
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <DetailRow label="Status" value={(reg.status || 'draft').replace(/_/g, ' ')} />
        <DetailRow label="Payment" value={paymentLabel(reg)} />
        <DetailRow label="Preferred name" value={a.preferred_first_name} />
        <DetailRow label="Gender" value={a.gender} />
        <DetailRow
          label="DOB"
          value={
            a.dob_or_age
              ? (isIsoDate(a.dob_or_age) && reg.computed_age != null
                  ? `${a.dob_or_age} (age ${reg.computed_age})`
                  : a.dob_or_age)
              : null
          }
          testId="regs-detail-dob"
        />
        <DetailRow label="Age" value={reg.computed_age != null ? String(reg.computed_age) : null} testId="regs-detail-age" />
        <DetailRow label="City" value={[a.city, a.state_region, a.country].filter(Boolean).join(', ')} />
        <DetailRow label="Occupation" value={a.occupation} />
        <DetailRow label="Languages" value={(a.languages || []).join(', ')} />
      </div>

      {a.about_me && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">About</div>
          <div className="text-neutral-700 whitespace-pre-line text-sm">{a.about_me}</div>
        </div>
      )}

      {(a.interests?.length || a.activity_interests?.length) ? (
        <div className="space-y-2">
          {a.interests?.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Interests</div>
              <div className="flex flex-wrap gap-1">{a.interests.map((x) => <Badge key={x} variant="outline">{x}</Badge>)}</div>
            </div>
          ) : null}
          {a.activity_interests?.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Activities</div>
              <div className="flex flex-wrap gap-1">{a.activity_interests.map((x) => <Badge key={x} variant="outline">{x}</Badge>)}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      {a.intentions?.length ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Intentions</div>
          <div className="flex flex-wrap gap-1">{a.intentions.map((x) => <Badge key={x} className="bg-secondary-50 text-secondary-800 border-secondary-200" variant="outline">{x.replace(/_/g, ' ')}</Badge>)}</div>
        </div>
      ) : null}

      {a.photos?.length ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Photos</div>
          <div className="grid grid-cols-3 gap-2">
            {a.photos.map((p, i) => (
              <PhotoTile key={p.id || i} photo={p} index={i} guestName={a.full_name || a.preferred_first_name || a.first_name || a.name || reg.email} />
            ))}
          </div>
        </div>
      ) : null}

      {(a.dietary_restrictions || a.food_allergies || a.accessibility_request) && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Notes</div>
          {a.dietary_restrictions && <div className="text-xs text-neutral-700"><span className="font-semibold">Dietary:</span> {a.dietary_restrictions}</div>}
          {a.food_allergies && (
            <div className="text-xs text-accent-700 bg-accent-50 border border-accent-200 rounded px-2 py-1">
              <span className="font-semibold">⚠ Allergies:</span> {a.food_allergies}
            </div>
          )}
          {a.accessibility_request && <div className="text-xs text-neutral-700"><span className="font-semibold">Accessibility:</span> {a.accessibility_request}</div>}
        </div>
      )}

      {a.marriage_prefs && (a.marriage_prefs.age_min || a.marriage_prefs.age_max || a.marriage_prefs.notes) && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Marriage preferences</div>
          <div className="text-xs text-neutral-700">
            {(a.marriage_prefs.age_min || a.marriage_prefs.age_max) && (
              <div>Age range: {a.marriage_prefs.age_min || '-'} to {a.marriage_prefs.age_max || '-'}</div>
            )}
            {a.marriage_prefs.notes && <div className="mt-1 whitespace-pre-line">{a.marriage_prefs.notes}</div>}
          </div>
        </div>
      )}

      {a.social_handles && Object.values(a.social_handles).some(Boolean) && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Social</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(a.social_handles).filter(([, v]) => v).map(([k, v]) => {
              const handle = String(v).trim().replace(/^@/, '');
              const platform = String(k).toLowerCase();
              const href =
                platform === 'instagram' ? `https://instagram.com/${encodeURIComponent(handle)}` :
                platform === 'linkedin' ? (handle.startsWith('http') ? handle : `https://linkedin.com/in/${encodeURIComponent(handle)}`) :
                platform === 'twitter' || platform === 'x' ? `https://x.com/${encodeURIComponent(handle)}` :
                platform === 'facebook' ? (handle.startsWith('http') ? handle : `https://facebook.com/${encodeURIComponent(handle)}`) :
                null;
              const chip = (
                <>
                  <span className="font-semibold capitalize">{k}:</span> {v}
                </>
              );
              return href ? (
                <a
                  key={k}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-user-social-${platform}-link`}
                  className="px-2 py-1 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-secondary-50 hover:border-secondary-300 transition-colors"
                >
                  {chip}
                </a>
              ) : (
                <span
                  key={k}
                  data-testid={`admin-user-social-${platform}-text`}
                  className="px-2 py-1 rounded-full border border-neutral-200 bg-neutral-50"
                >
                  {chip}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <RawAnswersSection answers={a} />

      <div className="pt-3 border-t border-neutral-100 text-[11px] text-neutral-400 space-y-0.5">
        <div>ID: <span className="font-mono">{reg.id}</span></div>
        {reg.ticket_code && <div>Ticket: <span className="font-mono font-semibold text-secondary-700">{reg.ticket_code}</span></div>}
        <div>Submitted: {reg.created_at && new Date(reg.created_at).toLocaleString()}</div>
        {reg.verified_email_at && <div>Email verified: {new Date(reg.verified_email_at).toLocaleString()}</div>}
        {reg.confirmed_at && <div>Confirmed: {new Date(reg.confirmed_at).toLocaleString()}</div>}
        {reg.checked_in_at && <div>Checked in: {new Date(reg.checked_in_at).toLocaleString()}</div>}
      </div>
    </div>
  );
};

const RawAnswersSection = ({ answers }) => {
  const [open, setOpen] = useState(false);
  const keys = answers && typeof answers === 'object' ? Object.keys(answers) : [];
  const pretty = useMemo(() => JSON.stringify(answers, null, 2), [answers]);
  if (keys.length === 0) return null;
  return (
    <div className="pt-3 border-t border-neutral-100" data-testid="regs-detail-raw-answers">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-primary-800"
          data-testid="regs-detail-raw-answers-toggle"
          aria-expanded={open}
        >
          {open
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />}
          Raw answers ({keys.length})
        </button>
        {open && (
          <CopyJsonButton
            value={pretty}
            testId="regs-detail-raw-answers-copy"
          />
        )}
      </div>
      {open && (
        <pre
          className="mt-2 text-[11px] leading-relaxed bg-neutral-50 border border-neutral-200 rounded p-3 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words"
          data-testid="regs-detail-raw-answers-body"
        >
          {pretty}
        </pre>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-secondary-700">{label}</div>
    <div
      className="text-sm text-primary-900"
      data-testid={testId}
    >
      {value || <span className="text-neutral-400">-</span>}
    </div>
  </div>
);

const isIsoDate = (v) => {
  if (typeof v !== 'string') return false;
  const s = v.trim();
  return s.length >= 10 && s[4] === '-' && s[7] === '-';
};

// Bug Y1: derive a truthful "Payment" label. `payment_status='not_required'`
// is only accurate when the event is genuinely free. If a paid tier is
// present and the guest hasn't verified yet, surface that clearly so the
// admin doesn't mistake unpaid RSVPs for comped ones.
const paymentLabel = (reg) => {
  const raw = reg.payment_status || 'not_required';
  const txn = reg.payment_txn || {};
  const priceCents = txn.amount_cents;
  const paidStates = new Set(['verified', 'paid']);
  const reviewStates = new Set(['auto_verified_pending_review']);
  const submittedStates = new Set(['user_marked_paid', 'instructions_viewed', 'under_review', 'pending']);
  if (raw === 'not_required') {
    if (priceCents && priceCents > 0) return 'required (unpaid)';
    return 'not required';
  }
  if (paidStates.has(raw)) return raw.replace(/_/g, ' ');
  if (reviewStates.has(raw)) return 'required (needs review)';
  if (submittedStates.has(raw)) return 'required (unpaid)';
  if (raw === 'rejected' || raw === 'failed') return `required (${raw})`;
  return raw.replace(/_/g, ' ');
};

// C.1b: Mark as paid panel. Rendered in the RegistrationDetail drawer for
// submitted / pending_payment RSVPs that never had a Zelle/Venmo/Stripe
// txn auto-verified. Collects method + amount + reference + audit note,
// then POSTs to /admin/registrations/{id}/mark-paid which funnels through
// the same finalizer used by auto-verify (ticket + email + points once).
const MARK_PAID_METHODS = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'cash',  label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' },
];

const MarkPaidPanel = ({ reg, onSubmit, busy }) => {
  const pt = reg.payment_txn || {};
  const defaultAmount = pt.amount_cents ?? reg.pricing_amount_cents ?? '';
  const [expanded, setExpanded] = React.useState(false);
  const [method, setMethod] = React.useState('zelle');
  const [amount, setAmount] = React.useState(defaultAmount === '' ? '' : String((Number(defaultAmount) / 100).toFixed(2)));
  const [reference, setReference] = React.useState('');
  const [note, setNote] = React.useState('');

  const submit = async () => {
    const cleanNote = (note || '').trim();
    if (cleanNote.length < 5) {
      alert('Please add an audit note (min 5 characters) explaining why you are marking this paid.');
      return;
    }
    if (!window.confirm(
      `Mark ${reg.email} as paid via ${method.toUpperCase()}? This will issue a ticket and send the confirmation email.`
    )) return;
    const payload = {
      method,
      admin_note: cleanNote,
      payment_reference: (reference || '').trim() || undefined,
    };
    if (amount !== '' && !isNaN(Number(amount))) {
      payload.amount_cents = Math.round(Number(amount) * 100);
    }
    await onSubmit?.(payload);
  };

  if (!expanded) {
    return (
      <div
        className="rounded-lg border border-secondary-200 bg-secondary-50/40 p-3 flex items-start justify-between gap-3"
        data-testid="mark-paid-collapsed"
      >
        <div className="text-xs text-secondary-900/90">
          <div className="font-semibold text-primary-900 text-sm">Payment stuck?</div>
          <div>Manually attach a Zelle / Venmo / cash payment so the guest gets their ticket.</div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setExpanded(true)}
          className="shrink-0"
          data-testid="mark-paid-expand-btn"
        >
          Mark as paid
        </Button>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-secondary-300 bg-white p-3 space-y-3"
      data-testid="mark-paid-panel"
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-primary-900">Mark as paid</div>
        <button
          type="button"
          className="text-xs text-neutral-500 hover:text-neutral-700"
          onClick={() => setExpanded(false)}
          data-testid="mark-paid-collapse-btn"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-secondary-700">Method</label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="mt-1" data-testid="mark-paid-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MARK_PAID_METHODS.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-secondary-700">Amount (USD)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="mt-1"
            data-testid="mark-paid-amount"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-secondary-700">
          Reference (memo, confirmation number, last 4)
        </label>
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. Zelle memo, Venmo transaction id"
          className="mt-1"
          data-testid="mark-paid-reference"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-secondary-700">
          Audit note (required, min 5 chars)
        </label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Why the auto-poller missed this. Kept in the audit log."
          className="mt-1"
          data-testid="mark-paid-note"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={submit}
          disabled={busy}
          className="flex-1 bg-success-600 hover:bg-success-700 text-white"
          data-testid="mark-paid-submit"
        >
          <CheckCircle2 className="h-4 w-4 mr-1" />
          {busy ? 'Marking paid…' : 'Confirm and issue ticket'}
        </Button>
      </div>
    </div>
  );
};

// Phase D: reject dialog with preset reason dropdown + notes textarea.
const REJECT_REASONS = [
  { value: 'amount_mismatch', label: 'Amount does not match the RSVP' },
  { value: 'wrong_sender', label: 'Wrong sender (unexpected email)' },
  { value: 'duplicate', label: 'Duplicate of a payment we already have' },
  { value: 'other', label: 'Other (see notes)' },
];

const RejectDialog = ({ reg, onClose, onSubmitted, eventId }) => {
  const [reason, setReason] = useState('amount_mismatch');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (reg) { setReason('amount_mismatch'); setNotes(''); }
  }, [reg]);

  const submit = async () => {
    if (!reg) return;
    setBusy(true);
    try {
      await api.post(`/admin/events/${eventId}/registrations/${reg.id}/reject`, {
        reason, notes,
      });
      await onSubmitted?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!reg} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="reject-dialog">
        <SheetHeader>
          <SheetTitle>Reject auto-matched payment</SheetTitle>
        </SheetHeader>
        {reg && (
          <div className="space-y-4 mt-6">
            <div className="text-sm text-neutral-600">
              Rejecting the auto-match for <strong>{reg.email}</strong>. The guest will be emailed with the reason below and a link to retry.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-secondary-700">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1" data-testid="reject-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-secondary-700">Notes (optional, shown to guest)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Give the guest a specific hint about what went wrong."
                className="mt-1"
                data-testid="reject-notes-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={busy} className="flex-1">Cancel</Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="flex-1 bg-accent-700 hover:bg-accent-800 text-white"
                data-testid="reject-submit-btn"
              >
                {busy ? 'Rejecting…' : 'Reject payment'}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// 1b: Force reissue dialog (super_admin only)
// Sends the confirmation email + QR for a confirmed reg. Preserves the
// existing ticket_code (Option B: no rotation). If ticket_code is missing,
// server mints one first.
// ---------------------------------------------------------------------------
const ReissueTicketDialog = ({ reg, onClose, onSubmitted }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (reg) { setReason(''); }
  }, [reg]);

  const submit = async () => {
    if (!reg) return;
    const trimmed = (reason || '').trim();
    if (trimmed.length < 5) {
      alert('Please describe why you are reissuing (min 5 characters).');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/registrations/${reg.id}/reissue-ticket`, { reason: trimmed },
      );
      alert(
        data.was_minted
          ? `Ticket ${data.ticket_code} minted and emailed to ${data.sent_to}.`
          : `Confirmation email re-sent to ${data.sent_to} (ticket ${data.ticket_code}).`
      );
      await onSubmitted?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reissue failed');
    } finally { setBusy(false); }
  };

  const missingTicket = reg && !reg.ticket_code;

  return (
    <Sheet open={!!reg} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="reissue-dialog">
        <SheetHeader>
          <SheetTitle>
            {missingTicket ? 'Force reissue (no ticket!)' : 'Force reissue ticket'}
          </SheetTitle>
        </SheetHeader>
        {reg && (
          <div className="space-y-4 mt-6">
            <div className="text-sm text-neutral-600">
              {missingTicket ? (
                <>
                  This confirmed registration is missing a ticket code. Reissuing
                  will mint a new code and email it to <strong>{reg.email}</strong>.
                </>
              ) : (
                <>
                  Re-sending the confirmation email to <strong>{reg.email}</strong>.
                  The existing ticket code
                  {' '}<span className="font-mono font-semibold text-secondary-700">{reg.ticket_code}</span>{' '}
                  will NOT change.
                </>
              )}
            </div>
            <div>
              <label
                htmlFor="reissue-reason-input"
                className="text-[10px] uppercase tracking-widest text-secondary-700"
              >
                Reason (audited, required)
              </label>
              <Textarea
                id="reissue-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Guest reports never receiving the confirmation email; verified inbox and spam folder."
                className="mt-1"
                data-testid="reissue-reason-input"
              />
              <div className="text-[10px] text-neutral-400 mt-1">
                Minimum 5 characters. Recorded in the admin audit trail.
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={busy}
                className="flex-1"
                data-testid="reissue-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={busy || (reason.trim().length < 5)}
                className={`flex-1 text-white ${missingTicket
                  ? 'bg-accent-700 hover:bg-accent-800'
                  : 'bg-secondary-700 hover:bg-secondary-800'}`}
                data-testid="reissue-submit-btn"
              >
                {busy ? 'Reissuing...' : (missingTicket ? 'Mint and send' : 'Reissue email')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AdminRegistrationsPage;
